import cn from 'classnames';
import {
  useState,
  useRef,
  useContext,
  useEffect
} from 'react';

import LdsSearch from '../LdsSearch/index.jsx';
import SideNavMenu from './SideNavMenu.jsx';
import { SideNavContext } from './SideNavContext.jsx';
import LdsContactLilly from '../LdsContactLilly/index.jsx';
import LdsIcon from '../LdsIcon/index.jsx';

export default function MenuAside({
  sideNavContactLillyExpand,
  sideNavContactLillyCollapse,
  postMenu,
  languageSelector,
  languagesFirst,
  logo,
  regulatoryLinks,
  hasSearch,
  searchEnabled,
  sideNavSearchText,
  preMenu,
  contactLilly,
  contactFirst,
  sideNavMenuText,
  sideNavExpandedText,
  sideNavCollapsedText,
  sideNavCloseText,
  mobileMenuExpanding,
  setSearchValue,
  onSearchSubmit,
  searchValue,
  routerLink,
  showMobileMenu,
  setShowMobileMenu,
  navContainerAriaLabel,
}) {
  const {
    navMenu,
    placeholderStyles,
    setPlaceholderStyles,
    scrollContainerOverflow,
    isMobile,
  } = useContext(SideNavContext);

  const [contactLillyExpanded, setContactLillyExpanded] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [isActive] = useState(false);
  const [sideNavScrollContainerHeightCorrection, setSideNavScrollContainerHeightCorrection] = useState(0);

  const sideNavRef = useRef(null);
  const topContainerRef = useRef(null);
  const searchContainerRef = useRef(null);
  const searchRef = useRef(null);
  const mobileActionsRef = useRef(null);
  const menuButtonRef = useRef(null);
  const menuContainerRef = useRef(null);
  const mobileMenuLogoRef = useRef(null);
  const closeButtonRef = useRef(null);
  const mobileRegulatoryLinksContainerRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const toggleContactLilly = () => setContactLillyExpanded(!contactLillyExpanded);

  useEffect(() => {
    if (scrollContainerOverflow === 'hidden' && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo(0, 0);
    }
  }, [scrollContainerOverflow, scrollContainerRef]);

  const setScrollHeight = () => {
    if (sideNavRef.current && topContainerRef.current && menuContainerRef.current) {
      const totalHeight = sideNavRef.current.offsetHeight;
      const topContainerHeight = topContainerRef.current.offsetHeight;
      menuContainerRef.current.style.height = `${totalHeight - topContainerHeight}px`;
    }
  };

  const setStickyHeight = () => {
    if (sideNavRef.current && topContainerRef.current && menuContainerRef.current) {
      const topContainerHeight = topContainerRef.current.offsetHeight;
      setPlaceholderStyles({ height: `${topContainerHeight}px` });
    }
  };

  const adjustScrollContainerHeight = () => {
    if (isMobile) {
      const mobileMenuHeaderBarHeight = mobileMenuLogoRef.current.offsetHeight;
      const regulatoryLinksContainerHeight = regulatoryLinks ? mobileRegulatoryLinksContainerRef.current.offsetHeight : 0;
      setSideNavScrollContainerHeightCorrection(mobileMenuHeaderBarHeight + regulatoryLinksContainerHeight);
    } else {
      setSideNavScrollContainerHeightCorrection(topContainerRef.current.offsetHeight);
    }
  };

  const setHeights = () => {
    adjustScrollContainerHeight();
    setStickyHeight();
    setScrollHeight();
  };

  // Adjust element heights
  useEffect(() => {
    setHeights();

    window.addEventListener('resize', setHeights);

    return () => {
      window.removeEventListener('resize', setHeights);
    };
  }, [
    sideNavRef,
    topContainerRef,
    menuContainerRef,
    navMenu,
    searchContainerRef,
  ]);

  const toggleMobileMenu = () => {
    if (showMobileMenu) {
      setShowMobileMenu(false);
      menuButtonRef.current.focus();
    } else {
      setShowMobileMenu(true);
      closeButtonRef.current.focus();
    }
  };

  return (
    <aside
      ref={sideNavRef}
      className={cn(
        'lds-side-nav',
        {
          active: isActive,
        }
      )}
    >
      <div
        style={placeholderStyles}
        className="lds-side-nav-placeholder"
      />
      <div className="lds-side-nav-container">
        {/* <!-- BEGIN "TOP CONTAINER" (NON-SCROLLING) --> */}
        <div
          ref={topContainerRef}
          className="lds-side-nav-top-container"
        >
          {/* <!-- "DESKTOP" LOGO SLOT --> */}
          <div
            className={cn(
              'lds-side-nav-logo',
              {
                'above-regulatory-links': !!regulatoryLinks,
              }
            )}
          >
            {logo}
          </div>

          {/* <!-- "DESKTOP" REGULATORY LINKS SLOT --> */}
          {
            regulatoryLinks
            && <div
              className="lds-side-nav-regulatory-links"
            >
              {regulatoryLinks}
            </div>
          }

          {/* <transition name="search"> */}
            { hasSearch
                && <div
                ref={searchContainerRef}
                className={cn(
                  'lds-side-nav-search',
                  {
                    enabled: searchEnabled || showMobileSearch,
                  }
                )}
              >
                <div className="lds-side-nav-search-container">
                  <LdsSearch ref={searchRef} value={searchValue} onChange={setSearchValue} onSubmit={onSearchSubmit} />
                </div>
              </div>
            }
          {/* </transition> */}

          {/* <!-- "MOBILE" ACTIONS --> */}
          <div
            ref={mobileActionsRef}
            className={cn(
              'lds-side-nav-mobile-actions',
              {
                'lds-side-nav-mobile-actions-expanded': showMobileSearch,
              }
            )}
          >
            {
              hasSearch
              && <button
                className={cn(
                  'lds-button-clear-style',
                  'lds-side-nav-search-button',
                  {
                    enabled: searchEnabled,
                  }
                )}
                onClick={() => setShowMobileSearch(!showMobileSearch)}
              >
                <LdsIcon
                  label={sideNavSearchText}
                  name="MagnifyingGlass"
                  inline
                />
              </button>
            }

            <button
              ref={menuButtonRef}
              className="lds-button-clear-style lds-side-nav-toggle"
              onClick={toggleMobileMenu}
            >
              <span className="lds-side-nav-toggle-text">
                {sideNavMenuText}
              </span>
              <LdsIcon
                name="List"
                decorative
                inline
              />
            </button>
            <div
              className="screen-reader-text"
              aria-live="polite"
            >
              { showMobileMenu ? sideNavExpandedText : sideNavCollapsedText }
            </div>
          </div>
        </div>
        {/* <!-- END "TOP CONTAINER" --> */}

        {/* <!-- BEGIN MENU CONTAINER" --> */}
        <nav
          ref={menuContainerRef}
          className={cn(
            'lds-side-nav-menu-container',
            {
              'mobile-expanding': mobileMenuExpanding,
            }
          )}
          aria-hidden={showMobileMenu || !isMobile ? 'false' : 'true'}
          style={{ visibility: showMobileMenu || !isMobile ? 'visible' : 'hidden' }}
          aria-label={navContainerAriaLabel}
        >
          {/* <!-- "MOBILE" LOGO SLOT AND CLOSE BUTTON --> */}
          <div
            ref={mobileMenuLogoRef}
            className="lds-side-nav-menu-container-logo"
          >
            <div className="row align-items-center justify-content-between">
              <div className="col-auto">
                {logo}
              </div>
              <div className="col-auto">
                <button
                  ref={closeButtonRef}
                  className="lds-button-clear-style lds-side-nav-toggle"
                  onClick={toggleMobileMenu}
                  aria-hidden={showMobileMenu || !isMobile ? 'false' : 'true'}
                  style={{ visibility: showMobileMenu || !isMobile ? 'visible' : 'hidden' }}
                >
                  <span className="lds-side-nav-toggle-text">
                    {sideNavCloseText}
                  </span>
                  <LdsIcon
                    name="X"
                    decorative
                    inline
                  />
                </button>
              </div>
            </div>
          </div>

          {/* <!-- "MOBILE" REGULATORY LINKS SLOT --> */}
          {
            regulatoryLinks
            && <div
              ref={mobileRegulatoryLinksContainerRef}
              className="lds-side-nav-menu-container-regulatory-links"
            >
              {regulatoryLinks}
            </div>
          }

          {/* <!-- "PRIME MENU CONTAINER (SCROLLING)" --> */}
          <div
            ref={scrollContainerRef}
            className="lds-side-nav-scroll-container"
            style={{
              height: `calc(100% - ${sideNavScrollContainerHeightCorrection}px)`,
              overflowY: scrollContainerOverflow,
            }}
          >
            {/* <!-- GENERIC UPPER INSERTION SLOT FOR CUSTOMIZATIONS --> */}
            {preMenu}

            {/* <!-- LANGUAGE SELECTOR -- UPPER INSERTION POINT --> */}
            {
              languageSelector && languagesFirst
              && <div className="lds-side-nav-language-selector upper">
                {languageSelector}
              </div>
            }

            {/* <!-- CONTACT LILLY -- UPPER INSERTION POINT --> */}
            { contactLilly && contactFirst
              && <div
                    className={cn(
                      'lds-side-nav-contact-lilly',
                      {
                        expanded: contactLillyExpanded,
                      }
                    )}
                    onClick={toggleContactLilly}
                >
                  <LdsContactLilly
                    forceMobile
                    closeOnDocumentClick={false}
                    btnClasses="lds-button-clear-style"
                    clearBtnClasses
                    {...contactLilly}
                  />
                  <LdsIcon
                    label={contactLillyExpanded ? sideNavContactLillyCollapse : sideNavContactLillyExpand }
                    className="lds-side-nav-menu-item-toggle-icon"
                    inline
                    name="CaretDown"
                    decorative
                  />
              </div>

            }

            {/* <!-- MAIN SIDE NAV MENU RENDERING COMPONENT --> */}

            <SideNavMenu
              parentId="SideNavParent"
              menu={navMenu}
              routerLink={routerLink}
            />

            {/* <!-- CONTACT LILLY -- LOWER INSERTION POINT --> */}
            {
              contactLilly && !contactFirst
              && <div
                    className={cn(
                      'lds-side-nav-contact-lilly',
                      {
                        expanded: contactLillyExpanded,
                      }
                    )}
                    onClick={toggleContactLilly}
                >
                  <LdsContactLilly
                    forceMobile
                    closeOnDocumentClick={false}
                    btnClasses="lds-button-clear-style"
                    clearBtnClasses
                    {...contactLilly}
                  />
                  <LdsIcon
                    label={contactLillyExpanded ? sideNavContactLillyCollapse : sideNavContactLillyExpand }
                    className="lds-side-nav-menu-item-toggle-icon"
                    inline
                    name="CaretDown"
                    decorative
                  />
              </div>
            }

            {/* <!-- LANGUAGE SELECTOR -- LOWER INSERTION POINT --> */}
            {
              languageSelector && !languagesFirst
              && <div className="lds-side-nav-language-selector lower">
                {languageSelector}
              </div>
            }

            {/* <!-- GENERIC LOWER INSERTION SLOT FOR CUSTOMIZATIONS --> */}
            {postMenu}
          </div>
        </nav>
        {/* <!-- END "MENU CONTAINER" --> */}
      </div>
    </aside>
  );
}
